import { useEffect, useState } from 'react';
import { DownloadSimple, X } from '@phosphor-icons/react';

/**
 * InstallPrompt
 * -------------
 * Subtle floating "Install App" CTA.
 * - Shows on Chrome/Android/Edge when browser fires `beforeinstallprompt`.
 * - Shows a manual iOS hint on iPhone Safari (no native event there).
 * - Auto-hides after the user installs or dismisses (localStorage memory).
 */
const DISMISS_KEY = 'lifeadmin_pwa_install_dismissed_v1';

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch {
      // ignore
    }
    if (isStandalone()) {
      setDismissed(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setShowIos(false);
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS: no beforeinstallprompt — show our own hint after a short delay
    if (isIos() && !isStandalone()) {
      const t = setTimeout(() => setShowIos(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === 'accepted' || choice?.outcome === 'dismissed') {
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    setDeferred(null);
    setShowIos(false);
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  if (dismissed) return null;

  // Android / Desktop Chrome — native install
  if (deferred) {
    return (
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/15 shadow-2xl px-4 py-3 text-white max-w-[92vw]"
        style={{ bottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
        data-testid="install-prompt-android"
      >
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-lg shadow-blue-500/40">
          <DownloadSimple size={18} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold leading-tight">Install LifeAdmin</p>
          <p className="text-[11px] text-slate-300 leading-tight">Quick access from your home screen</p>
        </div>
        <button
          onClick={handleInstall}
          data-testid="install-prompt-cta"
          className="rounded-full bg-white text-slate-900 px-4 py-2 text-[12px] font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          data-testid="install-prompt-dismiss"
          aria-label="Dismiss"
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }

  // iOS — manual instructions hint
  if (showIos) {
    return (
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/15 shadow-2xl px-4 py-3 text-white max-w-[92vw]"
        style={{ bottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
        data-testid="install-prompt-ios"
      >
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-lg shadow-blue-500/40 shrink-0">
          <DownloadSimple size={18} weight="bold" className="text-white" />
        </div>
        <p className="text-[12px] leading-snug text-slate-200 min-w-0">
          Tap <span className="font-semibold text-white">Share</span> then{' '}
          <span className="font-semibold text-white">"Add to Home Screen"</span> to install.
        </p>
        <button
          onClick={handleDismiss}
          data-testid="install-prompt-dismiss"
          aria-label="Dismiss"
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }

  return null;
}
